import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    TextField,
    Chip,
    Tab,
    Tabs,
    Alert,
    Autocomplete
} from '@mui/material';
import { X, User, Users, Calendar, UserPlus } from 'lucide-react';
import { usersApi, coursesApi } from '../../../services/api';

const AssignTaskDialog = ({ open, onClose, assignment, token }) => {
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [dueDate, setDueDate] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSearch = useCallback(async (term) => {
        if (!term || !term.trim()) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            if (tabValue === 0) {
                const results = await usersApi.searchUsers(term, token);
                setSearchResults(results || []);
            } else {
                const results = await usersApi.searchGroups(term, token);
                setSearchResults(results || []);
            }
        } catch (error) {
            setError(t('dashboard.courses.assign_task_dialog.search_error') || 'Ошибка при поиске');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [token, tabValue, t]);

    const handleAssign = async () => {
        if (selectedUsers.length === 0 && selectedGroups.length === 0) {
            setError(t('dashboard.courses.assign_task_dialog.select_users_or_groups') || 'Выберите пользователей или группы');
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            // Для каждого выбранного пользователя создаем назначение
            const assignmentPromises = [];

            for (const user of selectedUsers) {
                assignmentPromises.push(
                    (async () => {
                        try {
                            await coursesApi.assignTaskToUser({
                                user: user.id,
                                assignment: assignment.id,
                                due_date: dueDate || null
                            }, token);
                        } catch (error) {
                            throw error;
                        }
                    })()
                );
            }

            // TODO: Реализовать назначение для групп когда появится API

            await Promise.all(assignmentPromises);

            setSuccess(t('dashboard.courses.assign_task_dialog.success', {
                count: selectedUsers.length
            }) || `Задание успешно назначено ${selectedUsers.length} пользователям`);

            // Очищаем форму через 2 секунды
            setTimeout(() => {
                setSelectedUsers([]);
                setSelectedGroups([]);
                setSearchTerm('');
                setSearchResults([]);
                setDueDate('');
                setSuccess('');
                onClose();
            }, 2000);

        } catch (error) {
            setError(t('dashboard.courses.assign_task_dialog.assignment_error') || 'Ошибка при назначении задания');
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (searchTerm && searchTerm.trim()) {
            const timer = setTimeout(() => {
                handleSearch(searchTerm);
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setSearchResults([]);
        }
    }, [searchTerm, handleSearch]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setSearchResults([]);
        setSearchTerm('');
        setError('');
    };

    const handleSelectItem = (item) => {
        if (tabValue === 0) {
            if (!selectedUsers.find(u => u.id === item.id)) {
                setSelectedUsers([...selectedUsers, item]);
            }
        } else {
            if (!selectedGroups.find(g => g.id === item.id)) {
                setSelectedGroups([...selectedGroups, item]);
            }
        }
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleRemoveUser = (userId) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
    };

    const handleRemoveGroup = (groupId) => {
        setSelectedGroups(selectedGroups.filter(group => group.id !== groupId));
    };

    const handleClose = () => {
        if (!isSaving) {
            setSelectedUsers([]);
            setSelectedGroups([]);
            setSearchTerm('');
            setSearchResults([]);
            setDueDate('');
            setError('');
            setSuccess('');
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t('dashboard.courses.assign_task_dialog.title') || 'Назначить задание'}
                {assignment && (
                    <Typography variant="subtitle2" color="textSecondary">
                        {assignment.title}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab
                            icon={<User className="w-4 h-4 mr-2" />}
                            label={t('dashboard.courses.assign_task_dialog.users') || 'Пользователи'}
                            iconPosition="start"
                        />
                        <Tab
                            icon={<Users className="w-4 h-4 mr-2" />}
                            label={t('dashboard.courses.assign_task_dialog.groups') || 'Группы'}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('dashboard.courses.assign_task_dialog.due_date') || 'Срок выполнения'}
                    </Typography>
                    <TextField
                        type="date"
                        fullWidth
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        InputProps={{
                            startAdornment: <Calendar className="w-4 h-4 mr-2 text-gray-500" />,
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Autocomplete
                        freeSolo
                        options={searchResults}
                        inputValue={searchTerm}
                        onInputChange={(event, newValue) => {
                            setSearchTerm(newValue);
                        }}
                        getOptionLabel={(option) =>
                            typeof option === 'string'
                                ? option
                                : tabValue === 0
                                    ? option.username || ''
                                    : option.name || ''
                        }
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                {tabValue === 0 ? option.username : option.name}
                            </li>
                        )}
                        onChange={(event, value) => {
                            if (value && typeof value !== 'string') {
                                handleSelectItem(value);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={tabValue === 0
                                    ? (t('dashboard.courses.assign_task_dialog.search_users_placeholder') || 'Поиск пользователей...')
                                    : (t('dashboard.courses.assign_task_dialog.search_groups_placeholder') || 'Поиск групп...')
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isSearching && <CircularProgress size={20} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>

                {/* Selected Users/Groups */}
                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        {tabValue === 0
                            ? (t('dashboard.courses.assign_task_dialog.selected_users') || 'Выбранные пользователи')
                            : (t('dashboard.courses.assign_task_dialog.selected_groups') || 'Выбранные группы')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {tabValue === 0 ? (
                            selectedUsers.map(user => (
                                <Chip
                                    key={user.id}
                                    label={user.username}
                                    onDelete={() => handleRemoveUser(user.id)}
                                    deleteIcon={<X className="w-3 h-3" />}
                                />
                            ))
                        ) : (
                            selectedGroups.map(group => (
                                <Chip
                                    key={group.id}
                                    label={group.name}
                                    onDelete={() => handleRemoveGroup(group.id)}
                                    deleteIcon={<X className="w-3 h-3" />}
                                />
                            ))
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>
                    {t('common.cancel') || 'Отмена'}
                </Button>
                <Button
                    onClick={handleAssign}
                    variant="contained"
                    disabled={isSaving || (selectedUsers.length === 0 && selectedGroups.length === 0)}
                    startIcon={isSaving ? <CircularProgress size={16} /> : <UserPlus size={16} />}
                >
                    {isSaving
                        ? (t('common.assigning') || 'Назначение...')
                        : (t('common.assign') || 'Назначить')
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignTaskDialog;